import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import ConfirmModal from '../components/ConfirmModal';
import { reminderAPI, notificationAPI, ledgerAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiBell, FiPlus, FiCheck, FiTrash2, FiCalendar, FiClock, FiRepeat, FiAlertTriangle, FiCheckCircle, FiInbox } from 'react-icons/fi';
import { format } from 'date-fns';
import { formatAmount } from '../utils/currency';
import { useTone } from '../contexts/ToneContext';
import './Pages.css';

const SOURCE_LABELS = {
  all: 'All',
  user: 'User',
  credit_card: 'Credit Card',
  emi: 'EMI',
  vendor: 'Vendor',
  subscription: 'Subscription',
  system: 'System',
};

const STATUS_COLORS = {
  pending: { bg: '#ebf5fb', color: '#2471A3', label: 'Pending' },
  overdue: { bg: '#fde8e8', color: '#E74C3C', label: 'Overdue' },
  completed: { bg: '#d5f5e3', color: '#27AE60', label: 'Completed' },
  cancelled: { bg: '#f8f9fa', color: '#7F8C8D', label: 'Cancelled' },
};

const Reminders = () => {
  const { t } = useTone();
  const [activeTab, setActiveTab] = useState('reminders'); // 'reminders' | 'notifications'
  const [reminders, setReminders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [ledgers, setLedgers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [formData, setFormData] = useState({
    ledger_id: '',
    title: '',
    description: '',
    amount: '',
    reminder_date: '',
    reminder_time: '',
    repeat_type: 'none',
  });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line

  const fetchData = async () => {
    try {
      const [remindersRes, ledgersRes, notifRes] = await Promise.allSettled([
        reminderAPI.getAll(),
        ledgerAPI.getAll(),
        notificationAPI.getAll(),
      ]);
      if (remindersRes.status === 'fulfilled') {
        setReminders(remindersRes.value.data.data.reminders || []);
      }
      if (ledgersRes.status === 'fulfilled') {
        setLedgers(ledgersRes.value.data.data.ledgers || []);
      }
      if (notifRes.status === 'fulfilled') {
        setNotifications(notifRes.value.data.data.notifications || []);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        amount: formData.amount && formData.amount.trim() !== '' ? parseFloat(formData.amount) : null,
        reminder_time: formData.reminder_time || null,
      };

      if (editingId) {
        await reminderAPI.update(editingId, submitData);
        toast.success('Reminder updated');
      } else {
        await reminderAPI.create(submitData);
        toast.success('Reminder created');
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleComplete = async (id) => {
    try {
      const response = await reminderAPI.markComplete(id);
      const data = response.data?.data?.reminder;
      if (data?.recurring_created && data?.next_occurrence) {
        toast.success(`Completed! Next reminder scheduled for ${data.next_occurrence}`);
      } else {
        toast.success('Reminder marked as completed');
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to complete reminder');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await reminderAPI.delete(deleteTarget);
      toast.success('Reminder deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleMarkNotificationRead = async (id) => {
    try {
      await notificationAPI.markAsRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      toast.success('All notifications marked as read');
    } catch {
      toast.error('Failed to mark all as read');
    }
  };

  const resetForm = () => {
    setFormData({
      ledger_id: '',
      title: '',
      description: '',
      amount: '',
      reminder_date: '',
      reminder_time: '',
      repeat_type: 'none',
    });
    setEditingId(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dtStr) => {
    try {
      return format(new Date(dtStr), 'dd MMM, hh:mm a');
    } catch {
      return dtStr;
    }
  };

  // Filter reminders
  const filteredReminders = reminders.filter(r => {
    if (sourceFilter !== 'all' && r.source_type && r.source_type !== sourceFilter) return false;
    if (statusFilter && r.status && r.status !== statusFilter) return false;
    return true;
  });

  // Counts
  const overdueCount = reminders.filter(r => {
    const d = new Date(r.reminder_date);
    d.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return !r.is_completed && r.is_active && d < today;
  }).length;

  const unreadNotifCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner message={t('common.loading')} />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-container">
        <div className="page-header">
          <h1><FiBell style={{ marginRight: 8, verticalAlign: 'middle' }} />{t('reminders.title')}</h1>
          <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
            <FiPlus style={{ marginRight: 4, verticalAlign: 'middle' }} />
            {t('reminders.add')}
          </button>
        </div>

        {/* Tabs: Reminders | Notifications */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '2px solid var(--color-border, #eee)' }}>
          <button
            className={`btn ${activeTab === 'reminders' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('reminders')}
            style={{ borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', padding: '8px 16px' }}
          >
            <FiCalendar size={14} /> Due Items
            {overdueCount > 0 && <span style={{ background: '#E74C3C', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: '0.7rem', fontWeight: 700 }}>{overdueCount}</span>}
          </button>
          <button
            className={`btn ${activeTab === 'notifications' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab('notifications')}
            style={{ borderRadius: '8px 8px 0 0', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', padding: '8px 16px' }}
          >
            <FiBell size={14} /> Notifications
            {unreadNotifCount > 0 && <span style={{ background: '#E74C3C', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: '0.7rem', fontWeight: 700 }}>{unreadNotifCount}</span>}
          </button>
        </div>

        {/* ===== NOTIFICATIONS TAB ===== */}
        {activeTab === 'notifications' && (
          <div>
            {notifications.length === 0 ? (
              <EmptyState
                icon={<FiBell />}
                title="No Notifications"
                message="You'll see alerts here when reminders are due soon, due today, or overdue."
              />
            ) : (
              <>
                {unreadNotifCount > 0 && (
                  <div style={{ textAlign: 'right', marginBottom: 12 }}>
                    <button className="btn btn-sm btn-secondary" onClick={handleMarkAllRead}>
                      <FiCheckCircle size={14} style={{ marginRight: 4 }} /> Mark all as read
                    </button>
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {notifications.map(n => {
                    const typeStyle = {
                      overdue: { bg: '#fde8e8', border: '#E74C3C', icon: <FiAlertTriangle size={16} color="#E74C3C" /> },
                      due_today: { bg: '#fef9e7', border: '#F39C12', icon: <FiBell size={16} color="#F39C12" /> },
                      due_soon: { bg: '#ebf5fb', border: '#3498DB', icon: <FiClock size={16} color="#3498DB" /> },
                      budget_exceeded: { bg: '#fde8e8', border: '#C0392B', icon: <FiAlertTriangle size={16} color="#C0392B" /> },
                      budget_warning: { bg: '#fef9e7', border: '#E67E22', icon: <FiAlertTriangle size={16} color="#E67E22" /> },
                      generated: { bg: '#d5f5e3', border: '#27AE60', icon: <FiInbox size={16} color="#27AE60" /> },
                      system: { bg: '#f8f9fa', border: '#7F8C8D', icon: <FiInbox size={16} color="#7F8C8D" /> },
                    }[n.type] || { bg: '#f8f9fa', border: '#ccc', icon: <FiBell size={16} /> };

                    return (
                      <div
                        key={n.id}
                        className="card"
                        style={{
                          padding: '12px 16px',
                          borderLeft: `4px solid ${typeStyle.border}`,
                          background: n.is_read ? 'var(--color-bg-card, #fff)' : typeStyle.bg,
                          opacity: n.is_read ? 0.7 : 1,
                          cursor: n.is_read ? 'default' : 'pointer',
                        }}
                        onClick={() => !n.is_read && handleMarkNotificationRead(n.id)}
                      >
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                          <div style={{ flexShrink: 0, marginTop: 2 }}>{typeStyle.icon}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: n.is_read ? 400 : 600, color: 'var(--color-text)', marginBottom: 2 }}>
                              {n.message}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>
                              {formatDateTime(n.created_at)}
                            </div>
                          </div>
                          {!n.is_read && (
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: typeStyle.border, flexShrink: 0, marginTop: 6 }} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* ===== REMINDERS TAB ===== */}
        {activeTab === 'reminders' && (
          <>
            {/* Source Type Filter */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
              {Object.entries(SOURCE_LABELS).map(([key, label]) => (
                <button
                  key={key}
                  className={`btn btn-sm ${sourceFilter === key ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setSourceFilter(key)}
                  style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Status Filter */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
              <button
                className={`btn btn-sm ${statusFilter === '' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setStatusFilter('')}
                style={{ fontSize: '0.75rem', padding: '4px 10px' }}
              >
                All Status
              </button>
              {Object.entries(STATUS_COLORS).map(([key, { label }]) => (
                <button
                  key={key}
                  className={`btn btn-sm ${statusFilter === key ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setStatusFilter(key)}
                  style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                >
                  {label}
                </button>
              ))}
            </div>

            {filteredReminders.length === 0 ? (
              <EmptyState
                icon={<FiBell />}
                title={t('reminders.no_reminders')}
                message={t('reminders.create_first_msg')}
                actionLabel={t('reminders.add')}
                onAction={() => { resetForm(); setShowModal(true); }}
              />
            ) : (
              <div className="grid grid-2">
                {filteredReminders.map((reminder) => {
                  const isOverdue = !reminder.is_completed && reminder.is_active && new Date(reminder.reminder_date) < new Date(new Date().toDateString());
                  const statusKey = reminder.status || (reminder.is_completed ? 'completed' : isOverdue ? 'overdue' : 'pending');
                  const statusStyle = STATUS_COLORS[statusKey] || STATUS_COLORS.pending;

                  return (
                    <div key={reminder.id} className={`card reminder-card ${reminder.is_completed ? 'completed' : ''}`}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <h3 style={{ margin: 0 }}>{reminder.title}</h3>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          {/* Status badge */}
                          <span style={{
                            padding: '2px 8px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 600,
                            background: statusStyle.bg, color: statusStyle.color,
                          }}>
                            {statusStyle.label}
                          </span>
                          {/* Source badge */}
                          {reminder.source_type && reminder.source_type !== 'user' && (
                            <span style={{
                              padding: '2px 8px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 500,
                              background: '#f0f0f0', color: '#555',
                            }}>
                              {SOURCE_LABELS[reminder.source_type] || reminder.source_type}
                            </span>
                          )}
                        </div>
                      </div>
                      {reminder.description && <p style={{ margin: '0 0 8px', color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>{reminder.description}</p>}
                      <div className="reminder-details">
                        <div className="detail-item">
                          <FiCalendar size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                          <strong>{t('reminders.date')}:</strong> {formatDate(reminder.reminder_date)}
                        </div>
                        {reminder.reminder_time && (
                          <div className="detail-item">
                            <FiClock size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                            <strong>{t('reminders.time')}:</strong> {reminder.reminder_time}
                          </div>
                        )}
                        {reminder.amount && (
                          <div className="detail-item">
                            <strong>{t('reminders.amount')}:</strong> {formatAmount(reminder.amount)}
                          </div>
                        )}
                        {reminder.repeat_type && reminder.repeat_type !== 'none' && (
                          <div className="detail-item">
                            <FiRepeat size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                            <strong>{t('reminders.repeat')}:</strong> {reminder.repeat_type}
                          </div>
                        )}
                      </div>
                      <div className="card-actions">
                        {!reminder.is_completed && (
                          <button className="btn btn-sm btn-success" onClick={() => handleComplete(reminder.id)}>
                            <FiCheck style={{ marginRight: 4, verticalAlign: 'middle' }} />
                            {t('reminders.complete')}
                          </button>
                        )}
                        <button className="btn btn-sm btn-danger" onClick={() => setDeleteTarget(reminder.id)}>
                          <FiTrash2 style={{ marginRight: 4, verticalAlign: 'middle' }} />
                          {t('action.delete')}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {showModal && (
          <div className="modal" onClick={() => { setShowModal(false); resetForm(); }}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
              <h2>{editingId ? t('reminders.edit') : t('reminders.add')}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>{t('reminders.reminder_title')}</label>
                  <input type="text" name="title" value={formData.title} onChange={handleChange} placeholder="e.g. Rent Payment" required />
                </div>
                <div className="form-group">
                  <label>{t('reminders.description')}</label>
                  <textarea name="description" value={formData.description} onChange={handleChange} rows="3" placeholder="Additional details (optional)" />
                </div>
                <div className="form-group">
                  <label>{t('transactions.ledger')} *</label>
                  <select name="ledger_id" value={formData.ledger_id} onChange={handleChange} required>
                    <option value="">{t('reminders.select_ledger')}</option>
                    {ledgers.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>{t('reminders.amount')} ({t('common.optional')})</label>
                  <input type="text" inputMode="decimal" name="amount" value={formData.amount} onChange={handleChange} placeholder="0.00" />
                </div>
                <div className="form-group">
                  <label>{t('reminders.date')}</label>
                  <input type="date" name="reminder_date" value={formData.reminder_date} onChange={handleChange} required />
                </div>
                <div className="form-group">
                  <label>{t('reminders.time')} ({t('common.optional')})</label>
                  <input type="time" name="reminder_time" value={formData.reminder_time} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>{t('reminders.repeat')}</label>
                  <select name="repeat_type" value={formData.repeat_type} onChange={handleChange}>
                    <option value="none">None</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                    {t('action.cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingId ? t('action.update') : t('action.create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {deleteTarget && (
          <ConfirmModal
            title={t('reminders.delete')}
            message={t('reminders.delete_confirm')}
            onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)}
          />
        )}
      </div>
    </Layout>
  );
};

export default Reminders;
